import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

type Health = { environment?: string };
type SyncStatus = {
  environment: string;
  source: string;
  last_synced_at: string | null;
  status: string | null;
  trigger_configured: boolean;
};

function currentRole(): string | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? (JSON.parse(raw).role ?? null) : null;
  } catch {
    return null;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Thin bar shown everywhere except in production. Tells testers where they are,
 * that the data is a copy of production refreshed at each deployment, and that
 * nothing they do here reaches production. Superadmins can refresh the copy.
 */
export function EnvBanner() {
  const [environment, setEnvironment] = useState<string | null>(null);
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient.instance
      .get<Health>('/health')
      .then((r) => setEnvironment(r.data.environment ?? null))
      .catch(() => setEnvironment(null));
    apiClient.instance
      .get<SyncStatus>('/sync/status')
      .then((r) => setSync(r.data))
      .catch(() => setSync(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!environment || environment === 'production') return;
    const base = document.title.replace(/^\[[A-Z]+\]\s*/, '');
    document.title = `[${environment.toUpperCase()}] ${base}`;
  }, [environment]);

  if (!environment || environment === 'production') return null;

  const label = environment.toUpperCase();
  const canTrigger = currentRole() === 'superadmin' && sync?.trigger_configured;

  const trigger = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await apiClient.instance.post('/sync/trigger');
      setMessage('Synchronisation lancée, les données seront rafraîchies dans quelques minutes.');
    } catch {
      setMessage("La synchronisation n'a pas pu être lancée.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      className="w-full bg-amber-400 text-amber-950 text-xs font-medium tracking-wide text-center py-1 px-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
    >
      <span className="font-semibold">Environnement {label}</span>
      <span>
        {sync?.last_synced_at
          ? `Données copiées depuis la ${sync.source === 'production' ? 'production' : sync.source} le ${formatDate(sync.last_synced_at)}.`
          : 'Données de test.'}{' '}
        Rien de ce qui est saisi ici ne remonte en production.
      </span>
      {canTrigger && (
        <button
          type="button"
          onClick={trigger}
          disabled={busy}
          className="rounded bg-amber-950 text-amber-50 px-2 py-0.5 text-[11px] font-semibold hover:bg-amber-900 disabled:opacity-50"
        >
          {busy ? 'Lancement…' : 'Rafraîchir depuis la prod'}
        </button>
      )}
      {message && <span className="italic">{message}</span>}
    </div>
  );
}
